/**
 * town_life.ts — the TOWN LIFE wave's client-side numbers (2026-09-24). MIRRORS of the server's constants
 * (server/src/lib/player_bounty_ops.ts, ground_contract_ops.ts, town_fx_ops.ts, player_post_ops.ts) so a window
 * previews exactly what a posting will cost; the server stays the authority. `verify:town-life` reads the server
 * files and pins every value here against them.
 */
export const BOUNTY_MIN_REWARD = 250;
export const BOUNTY_MAX_REWARD = 5_000_000;
export const BOUNTY_FEE_BPS = 1000;
export const BOUNTY_MIN_FEE = 25;
export const BOUNTY_HOURS_DEFAULT = 72;
export const BOUNTY_HOURS_MIN = 6;
export const BOUNTY_HOURS_MAX = 168;
export const HUNTERS_PER_BOUNTY = 3;
export const TAKES_PER_HUNTER = 2;

export const CONTRACT_MIN_REWARD = 20;
export const CONTRACT_MAX_REWARD = 2_000_000;
export const CONTRACT_FEE_BPS = 500;
export const CONTRACT_MIN_FEE = 5;
export const CONTRACT_HOURS_MIN = 1;
export const CONTRACT_HOURS_MAX = 72;
export const CONTRACT_HOURS_DEFAULT = 12;
export const PARCEL_MAX_ITEMS = 8;
export const ESCORT_NEAR_PLAYER_M = 60;

export const FIREWORK_SOV = 5;
export const FIREWORK_SHOW_SOV = 60;
export const FIREWORK_KINDS = ['peony', 'willow', 'ring', 'crossette', 'palm', 'strobe'] as const;
export type FireworkKind = (typeof FIREWORK_KINDS)[number];
export const FIREWORK_LABELS: Record<FireworkKind, string> = {
  peony: 'Peony', willow: 'Willow', ring: 'Ring', crossette: 'Crossette', palm: 'Palm', strobe: 'Strobe',
};
/** Eight palettes (the server stores an index 0..7): [core, trail] hex. Colours are the SKY's, not the UI's. */
export const FIREWORK_PALETTES: Array<{ name: string; a: number; b: number }> = [
  { name: 'Gold', a: 0xffd36b, b: 0xff9a2e },
  { name: 'Crimson', a: 0xff4a3c, b: 0xffb0a0 },
  { name: 'Emerald', a: 0x4dff8a, b: 0xc8ffd8 },
  { name: 'Azure', a: 0x4aa8ff, b: 0xd0e8ff },
  { name: 'Violet', a: 0xc070ff, b: 0xffd0ff },
  { name: 'Silver', a: 0xf2f4ff, b: 0x9fb4ff },
  { name: 'Aurelian', a: 0xffc94a, b: 0xfff1c0 },
  { name: 'Borean', a: 0x8fd8ff, b: 0xe8fbff },
];
export const CAMP_LIFE_S = 30 * 60;
export const CAMP_SPACING_M = 40;
export const WARMTH_GAP_S = 10 * 60;

export const DUTY_PING_S = 20;
export const SERVE_REACH_M = 5;

/** The bounty guild's fee on a posting (10 %, never under 25). */
export function bountyFee(reward: number): number {
  return Math.max(BOUNTY_MIN_FEE, Math.floor((reward * BOUNTY_FEE_BPS) / 10_000));
}
/** The board's fee on a contract (5 %, never under 5). */
export function contractFee(reward: number): number {
  return Math.max(CONTRACT_MIN_FEE, Math.floor((reward * CONTRACT_FEE_BPS) / 10_000));
}

/** "3 h 20 m" / "12 m" / "45 s" — a countdown in words. */
export function untilText(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s >= 3600) { const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60); return m ? `${h} h ${m} m` : `${h} h`; }
  if (s >= 60) return `${Math.floor(s / 60)} m ${String(s % 60).padStart(2, '0')} s`;
  return `${s} s`;
}
/** 8 compass points, bearing 0 = north (−Z), clockwise. */
export function compassOf(dx: number, dz: number): string {
  const a = (Math.atan2(dx, -dz) * 180) / Math.PI;
  const i = Math.round(((a + 360) % 360) / 45) % 8;
  return ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'][i];
}
/** A race time as m:ss.t */
export function raceTime(ms: number): string {
  const t = Math.max(0, ms) / 1000;
  const m = Math.floor(t / 60);
  const s = t - m * 60;
  return `${m}:${s.toFixed(1).padStart(4, '0')}`;
}
